import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Modal from 'react-modal';
import { fetchAllowanceSplits, updateAllowanceSplits } from '../../../api';
import MyButton from '../../basic/MyButton';
import MySlider from '../../basic/MySlider';


import './modalEditSplits.css';

Modal.setAppElement('#root');

// Calculate the sidebar width in pixels (assuming 1rem = 16px)
const sidebarWidth = 16 * 16; // 16rem * 16px/rem = 256px

const ModalEditSplits = ({ isOpen, onClose, childId, onSuccess }) => {
  const [splits, setSplits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [totalPercentage, setTotalPercentage] = useState(0);
  const [isFormChanged, setIsFormChanged] = useState(false);

  const calculateTotalPercentage = useCallback((currentSplits) => {
    const total = currentSplits.reduce((sum, split) => sum + (parseFloat(split.percentage) || 0), 0);
    setTotalPercentage(parseFloat(total.toFixed(1)));
  }, []);

  const fetchSplits = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchAllowanceSplits(childId);
      const formattedSplits = data.map(split => ({
        accountId: split.account_id,
        accountName: split.account_name,
        percentage: split.allowance_splits[0]?.percentage || 0
      }));
      setSplits(formattedSplits);
      calculateTotalPercentage(formattedSplits);
    } catch (err) {
      console.error("Error fetching splits:", err);
      setError('Failed to load allowance splits. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [childId, calculateTotalPercentage]);

  useEffect(() => {
    if (isOpen) {
      setIsFormChanged(false);
      fetchSplits();
    }
  }, [isOpen, fetchSplits]);

  const handlePercentageChange = (accountId, value) => {
    const updatedSplits = splits.map(split => 
      split.accountId === accountId 
        ? { ...split, percentage: value }
        : split
    );
    setSplits(updatedSplits);
    calculateTotalPercentage(updatedSplits);
    setIsFormChanged(true);
  };

  const handleSubmit = async () => {
    if (totalPercentage !== 100) {
      setError('Total percentage must equal 100%');
      return;
    }

    setLoading(true);
    setError('');

    const formattedSplits = splits.map(split => ({
      account_id: split.accountId,
      percentage: parseFloat(split.percentage) || 0
    }));

    try {
      await updateAllowanceSplits(childId, formattedSplits);
      onSuccess('Splits updated successfully.');
      onClose();
    } catch (err) {
      setError('Failed to update splits. Please try again.');
      console.error("Error in update of splits: ", err);
    } finally {
      setLoading(false);
    }
  };

  const distributeEvenly = () => {
    if (splits.length === 0) return;
  
    const evenPercentage = Math.floor(100 / splits.length);
    let totalPercentage = evenPercentage * splits.length;
    const difference = 100 - totalPercentage;
  
    const updatedSplits = splits.map((split, index) => {
      if (index === 0) {
        return { ...split, percentage: evenPercentage + difference };
      }
      return { ...split, percentage: evenPercentage };
    });
  
    setSplits(updatedSplits);
    calculateTotalPercentage(updatedSplits);
    setIsFormChanged(true);
  };
  
  const resetSplits = () => {
    fetchSplits();
    setIsFormChanged(false);
  };

  const useGridLayout = useMemo(() => splits.length > 5, [splits.length]);

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel="Edit Income Splits"
      className="esm-modal"
      overlayClassName="esm-overlay"
      // ... (keep the existing modal properties)
    >
      <div className="esm-content">
        <h2 className="esm-title">Income Splits</h2>
        <p className="esm-description">
          Adjust how deposits are split between accounts. Total must equal 100%.
        </p>
        {loading ? (
          <div className="esm-loading-spinner" />
        ) : (
          <>
            <TotalIndicator percentage={totalPercentage} />
            <div className="esm-accounts-list">
              {splits.map(split => (
                <AccountCard
                  key={split.accountId}
                  account={split}
                  onPercentageChange={handlePercentageChange}
                />
              ))}
            </div>
            <div className="esm-actions">
              <MyButton
                text="Distribute Evenly"
                onClick={distributeEvenly}
                backgroundColor="white"
                color="#333"
                hoverBackgroundColor="#e9ecef"
                hoverColor="#333"
              />
              <MyButton
                text="Reset"
                onClick={resetSplits}
                backgroundColor="white"
                color="#333"
                hoverBackgroundColor="#e9ecef"
                hoverColor="#333"
              />
              <MyButton
                text="Save Changes"
                onClick={handleSubmit}
                disabled={loading || totalPercentage !== 100 || !isFormChanged}
                color="white"
                hoverColor="white"
              />
              <MyButton
                text="Cancel"
                onClick={onClose}
                backgroundColor="white"
                color="#dc3545"
                hoverBackgroundColor="#f8f8f8"
                hoverColor="#c82333"
              />
            </div>
            {error && <div className="esm-error-message">{error}</div>}
          </>
        )}
      </div>
    </Modal>
  );
};

const AccountCard = ({ account, onPercentageChange }) => {
  const handleSliderChange = (value) => {
    onPercentageChange(account.accountId, value);
  };

  const handleInputChange = (e) => {
    const value = Math.min(100, Math.max(0, e.target.value));
    onPercentageChange(account.accountId, value);
  };

  return (
    <div className="esm-account-card">
      <h3 className="esm-account-card__name">{account.accountName}</h3>
      <MySlider
        value={account.percentage}
        onChange={handleSliderChange}
        min={0}
        max={100}
        step={1}
        showLabels={false}
        showValue={false}
      />
      <input
        className="esm-account-card__input"
        type="number"
        min="0"
        max="100"
        value={account.percentage}
        onChange={handleInputChange}
      />
      <div 
        className="esm-account-card__circle" 
        style={{ background: `conic-gradient(#3498db 0deg, #3498db ${account.percentage * 3.6}deg, #eee ${account.percentage * 3.6}deg)` }}
      >
        {account.percentage}%
      </div>
    </div>
  );
};

const TotalIndicator = ({ percentage }) => {
  const color = percentage === 100 ? 'green' : percentage > 100 ? 'red' : 'purple';

  return (
    <div className="esm-total-indicator" style={{ '--color': color }}>
      <svg viewBox="0 0 36 36">
        <path
          d="M18 2.0845
            a 15.9155 15.9155 0 0 1 0 31.831
            a 15.9155 15.9155 0 0 1 0 -31.831"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeDasharray={`${percentage}, 100`}
        />
        <text x="18" y="20.35" textAnchor="middle" fontSize="55%">{percentage}%</text>
      </svg>
    </div>
  );
};

export default ModalEditSplits;